import { useEffect, useState } from "react";
import JobLink from "@/components/JobLink";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Download, ArrowUpFromLine, ArrowDownToLine } from "lucide-react";
import { toast } from "sonner";
import DatePicker from "@/components/DatePicker";

const inr = (n) => (n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

/**
 * Stores Issue Register — surfaces ALL stores-io transactions with filters for
 * category (Raw / Ink / Varnish / All) so ink & varnish consumption sits
 * alongside paper. Backed by GET /api/inventory/stores-io.
 */
export default function StoresIssueRegisterReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    date_from: "",
    date_to: "",
    material_category: "all",
    dept: "all",
    direction: "all",
    q: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to) params.date_to = f.date_to;
      if (f.material_category !== "all") params.material_category = f.material_category;
      if (f.dept !== "all") params.dept = f.dept;
      if (f.direction !== "all") params.direction = f.direction;
      params.limit = 5000;
      const { data } = await api.get("/inventory/stores-io", { params });
      const filtered = f.q.trim()
        ? data.filter((r) => {
            const n = f.q.toLowerCase();
            return (r.material_name || "").toLowerCase().includes(n)
              || (r.material_code || "").toLowerCase().includes(n)
              || (r.bpr_no || "").toLowerCase().includes(n)
              || (r.roll_id || "").toLowerCase().includes(n);
          })
        : data;
      setRows(filtered);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f.date_from, f.date_to, f.material_category, f.dept, f.direction]);

  // Totals split by category so ink/varnish/paper stand apart in the summary
  const catTotals = rows.reduce((acc, r) => {
    const cat = r.material_category || "unknown";
    const q = Number(r.qty || 0), ret = Number(r.return_qty || 0);
    if (!acc[cat]) acc[cat] = { out: 0, ret: 0, in: 0 };
    if (r.direction === "outward") { acc[cat].out += q; acc[cat].ret += ret; }
    else { acc[cat].in += q; }
    return acc;
  }, {});
  const grandOut = Object.values(catTotals).reduce((s, c) => s + c.out, 0);
  const grandRet = Object.values(catTotals).reduce((s, c) => s + c.ret, 0);
  const grandIn = Object.values(catTotals).reduce((s, c) => s + c.in, 0);

  const downloadXlsx = async () => {
    const p = new URLSearchParams();
    if (f.date_from) p.set("date_from", f.date_from);
    if (f.date_to) p.set("date_to", f.date_to);
    if (f.material_category !== "all") p.set("material_category", f.material_category);
    if (f.dept !== "all") p.set("dept", f.dept);
    if (f.direction !== "all") p.set("direction", f.direction);
    try {
      // Blob download to keep the JWT header on the request
      const res = await api.get(`/inventory/stores-io/export.xlsx?${p.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `stores-issue-register_${f.date_from || "all"}_to_${f.date_to || "today"}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
    // eslint-disable-next-line no-unused-vars
    void API_BASE;
  };

  return (
    <div data-testid="report-stores-issue-register">
      <div className="border border-neutral-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-caps block mb-1">From</label>
          <div className="w-40"><DatePicker value={f.date_from} onChange={(v) => setF({ ...f, date_from: v })} testId="sir-from" /></div>
        </div>
        <div>
          <label className="label-caps block mb-1">To</label>
          <div className="w-40"><DatePicker value={f.date_to} onChange={(v) => setF({ ...f, date_to: v })} testId="sir-to" /></div>
        </div>
        <div>
          <label className="label-caps block mb-1">Category</label>
          <Select value={f.material_category} onValueChange={(v) => setF({ ...f, material_category: v })}>
            <SelectTrigger data-testid="sir-category" className="rounded-sm w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="raw">Paper / Raw</SelectItem>
              <SelectItem value="ink">Ink</SelectItem>
              <SelectItem value="varnish">Varnish</SelectItem>
              <SelectItem value="consumable">Consumable</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="label-caps block mb-1">Dept</label>
          <Select value={f.dept} onValueChange={(v) => setF({ ...f, dept: v })}>
            <SelectTrigger data-testid="sir-dept" className="rounded-sm w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Depts</SelectItem>
              <SelectItem value="PRINTING">Printing</SelectItem>
              <SelectItem value="SLITTING">Slitting</SelectItem>
              <SelectItem value="PACKING">Packing</SelectItem>
              <SelectItem value="INSPECTION">Inspection</SelectItem>
              <SelectItem value="RETURN">Return</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="label-caps block mb-1">Direction</label>
          <Select value={f.direction} onValueChange={(v) => setF({ ...f, direction: v })}>
            <SelectTrigger data-testid="sir-direction" className="rounded-sm w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Both</SelectItem>
              <SelectItem value="outward">Outward</SelectItem>
              <SelectItem value="inward">Inward</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-56">
          <label className="label-caps block mb-1">Search</label>
          <Input data-testid="sir-search" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="material / code / BPR / roll ID" className="rounded-sm" />
        </div>
        <Button data-testid="sir-apply" onClick={load} variant="outline" className="rounded-sm">Apply</Button>
        <Button data-testid="sir-export" onClick={downloadXlsx} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00] gap-1.5">
          <Download size={13} /> Export XLSX
        </Button>
      </div>

      {/* KPI strip — per-category totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
        <TotalCard label="Total Issued" value={inr(grandOut)} tone="warn" />
        <TotalCard label="Returned to Store" value={inr(grandRet)} tone="ok" />
        <TotalCard label="Inward From Dept" value={inr(grandIn)} tone="ok" />
        <TotalCard label="Rows" value={rows.length} tone="neutral" />
        <TotalCard label="Net Consumed" value={inr(grandOut - grandRet - grandIn)} tone="danger" />
      </div>

      {Object.keys(catTotals).length > 0 && (
        <div className="border border-neutral-200 bg-white p-3 mb-4">
          <div className="label-caps text-[10px] mb-2">By Category</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
            {Object.entries(catTotals).map(([cat, t]) => (
              <div key={cat} className="flex items-center justify-between border-b border-neutral-100 pb-1.5">
                <span className="uppercase text-[10px] tracking-widest text-neutral-500">{cat}</span>
                <span className="font-mono-num">
                  <span className="text-amber-700">{inr(t.out)}</span>
                  <span className="text-neutral-400 mx-1">·</span>
                  <span className="text-emerald-700">{inr(t.ret + t.in)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="sir-table">
          <thead className="bg-neutral-50 label-caps text-[10px]">
            <tr>
              <th className="text-left px-3 py-2.5">Date</th>
              <th className="text-left px-3 py-2.5">Material</th>
              <th className="text-left px-3 py-2.5">Code</th>
              <th className="text-left px-3 py-2.5">Cat.</th>
              <th className="text-right px-3 py-2.5">Width</th>
              <th className="text-right px-3 py-2.5">Qty</th>
              <th className="text-left px-3 py-2.5">UOM</th>
              <th className="text-right px-3 py-2.5">Return</th>
              <th className="text-left px-3 py-2.5">Dir.</th>
              <th className="text-left px-3 py-2.5">Dept</th>
              <th className="text-left px-3 py-2.5">BPR</th>
              <th className="text-left px-3 py-2.5">Roll ID</th>
              <th className="text-left px-3 py-2.5">By</th>
              <th className="text-left px-3 py-2.5">Remarks</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={14} className="px-4 py-12 text-center text-neutral-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={14} className="px-4 py-12 text-center text-neutral-400" data-testid="sir-empty">No transactions in this window.</td></tr>}
            {!loading && rows.slice(0, 800).map((r) => (
              <tr key={r.id} className="border-t border-neutral-100 data-row" data-testid={`sir-row-${r.id}`}>
                <td className="px-3 py-2 font-mono-num text-xs text-neutral-600">{(r.date || "").slice(0, 10)}</td>
                <td className="px-3 py-2 font-medium">{r.material_name}</td>
                <td className="px-3 py-2 font-mono-num text-xs">{r.material_code || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  {r.material_category && (
                    <span className="uppercase text-[10px] tracking-widest text-neutral-500 border border-neutral-300 px-1 rounded-sm">{r.material_category}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono-num">{r.width ?? "—"}</td>
                <td className="px-3 py-2 text-right font-mono-num font-semibold">{inr(r.qty)}</td>
                <td className="px-3 py-2 text-xs">{r.uom}</td>
                <td className="px-3 py-2 text-right font-mono-num text-emerald-700">{r.return_qty != null ? inr(r.return_qty) : "—"}</td>
                <td className="px-3 py-2 text-xs">
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-widest ${r.direction === "inward" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                    {r.direction === "inward" ? <ArrowDownToLine size={10} /> : <ArrowUpFromLine size={10} />}
                    {r.direction || "outward"}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">{r.dept_to || "—"}</td>
                <td className="px-3 py-2 font-mono-num text-xs"><JobLink number={r.bpr_no} tab="materials" /></td>
                <td className="px-3 py-2 font-mono-num text-xs">{r.roll_id || "—"}</td>
                <td className="px-3 py-2 text-xs text-neutral-600">{r.user_name || "—"}</td>
                <td className="px-3 py-2 text-xs text-neutral-600 max-w-[180px] truncate">{r.remarks || r.notes || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 800 && (
        <div className="text-[11px] text-neutral-500 mt-2">Showing first 800 rows. Refine your filters or export XLSX for the full data set.</div>
      )}
    </div>
  );
}

function TotalCard({ label, value, tone }) {
  const cls = tone === "danger" ? "text-rose-700" : tone === "warn" ? "text-amber-700" : tone === "ok" ? "text-emerald-700" : "text-neutral-900";
  return (
    <div className="border border-neutral-200 bg-white px-4 py-3">
      <div className="label-caps mb-1 text-[10px]">{label}</div>
      <div className={`font-mono-num text-xl font-bold ${cls}`}>{value}</div>
    </div>
  );
}
